import { globals } from 'config/globals';
import DelayHelper from 'utils/timers';
import { device } from 'utils/device';
import { Simple } from 'utils/analytics';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	FLAG_ACTIVE
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import DepartmentColours from 'app/views/home/webgl/particlesOptimised/departments/DepartmentColours';
import { PhaseRunner } from 'app/views/home/webgl/particlesOptimised/departments/PhaseRunner';
import { GROUP_SPACES, SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import { SineGridState } from 'app/views/home/webgl/particlesOptimised/departments/spaces/phases/SineGridPhase';
import { createSpacesPhaseEntries } from 'app/views/home/webgl/particlesOptimised/departments/spaces/spacesPhaseEntries';

import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { PhaseEntry } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * SpacesDepartment (particles-optimised) — FORK of
 * webgl/particles/departments/SpacesDepartment.ts + SpacesParticleProcessor,
 * restructured onto the phase framework (phases.types.ts / PhaseRunner):
 * the reference's string-keyed stage machine (_spacesBoxStages +
 * _nextSpacesStage if-chain) becomes the ordered phase list in
 * spaces/spacesPhaseEntries.ts (THE place to reorder/re-time/add phases),
 * one file per stage under departments/spaces/phases/. Phase BODIES are the
 * verbatim lane ports — the framework changed structure, not math.
 *
 * The LIVE cycle (reference _spacesBoxStages, timings verbatim):
 *   Rain 350 -> PixelImagesLady 350 -> SquarePaths 300 -> RandomLerpLines
 *   550 -> SineWaveGridNormal 200 -> SineWaveGridRandom 300 ->
 *   SineWaveGridWavey 300 -> OrganicLines 640 -> OrganicLinesQuick 360
 *
 * Reference stage methods NOT in that list are UNREACHABLE (nothing else
 * calls them) — dead code, documented-not-ported: PerspectiveOrbit,
 * WhoozyTargets, PixelImagesWave/Leg/Moth, LineDropThenPhysicsBurst,
 * PerspectiveGrid, LeftDrop, BurstUp, BasicGrid, spacesDIcon and
 * initSpacesBoxIcon. NOTE this list is also where every
 * `depth.active = true` call in the spaces/systems processors lives — no
 * LIVE home stage enables ParticleDepth, which is why BEHAVIOUR_DEPTH stays
 * unset (see the facade's kernel-order note).
 *
 * What this shell keeps (department-level, not phase-level):
 *  - the runner hooks = the reference _nextSpacesStage preamble/postamble:
 *    pool-wide followParticle reset + the shared script-group cancel + the
 *    next-phase analytics (onTransition), then the colour-cycle change
 *    (onStarted) — the DepartmentColours fork, upward colouriseSpacesItems
 *    included;
 *  - per-tick plumbing: container positioning (the particleContainersX.spaces
 *    seam), the spaces mask redraw, the ReflectionFilter area/boundary;
 *  - scene lifecycle: the staggered start beats (ungrouped DelayHelpers,
 *    reference bug-compat — a stop inside the 40/80/1000ms window does not
 *    cancel them), the counter presets (120 / frames - 90) and the stop
 *    sweep; stopAsActiveScene maps to runner.stop() = hide() + reset() on
 *    the current phase, so re-entry re-forms cleanly;
 *  - physics onTargetReached routing into the context's handler registry.
 *
 * Framework-vs-reference timing notes (analysed, no observable difference):
 *  - the reference ticks its shared ComboCounter BEFORE the stage-advance
 *    check; here each organic phase ticks its own counter after the advance
 *    — the only divergence would be a combo entry landing exactly on a
 *    boundary frame, and the live timelines end >1s before their stages do;
 *  - globals.skipNormalParticleUpdates writes are the object pipeline's
 *    one-frame-skip hack — not ported (stage-4 convention).
 *
 * Other reference quirks kept/documented:
 *  - _homeSpacesState's body after its early `return` is dead — only the
 *    repell-target + trackMouse prefix is live (both no-op as lanes);
 *  - the reference pool is getParticlePool's slice(lower, upper) — an
 *    off-by-one that DROPS the partition's last particle; this fork uses
 *    the full range (stage-5 screens convention);
 *  - toWork/fromWorkToHome (gsap container tweens) are DEAD in the
 *    reference — HomeField.animateToWork/animateFromWork and
 *    DepartmentSwitcher.fromWorkToHome have no callers; the live work
 *    re-entry is showFromWork's stop + restart (stage-7 audit,
 *    documented-not-ported);
 *  - the ReflectionFilter is created once, 1s after scene start, skipped
 *    entirely in globals.slowFpsMode — headless SwiftShader TRIPS that
 *    gate, so filter verification must run HEADED (docs/DECISIONS.md).
 */
export default class SpacesDepartment implements DepartmentScene {
	private _deps: DepartmentDeps;
	private _core: ParticleCore;
	private _ctx: SpacesContext;
	private _colours: DepartmentColours;
	private _runner: PhaseRunner;

	private _active: boolean = false;
	private _hasBeenStarted: boolean = false;

	private _relectionFilter: any = null; //PIXI.filters.ReflectionFilter
	private _filterShape: PIXI.Graphics = null;

	//The ordered phase list + reference frames (see spacesPhaseEntries.ts —
	//THE place to reorder/re-time/add spaces phases):
	private _entries: Array<PhaseEntry>;

	constructor(deps: DepartmentDeps) {
		this._deps = deps;
		this._core = deps.core;
		this._ctx = new SpacesContext(deps.core, deps);
		this._colours = new DepartmentColours(deps.core, deps.scripts, deps);

		this._deps.stage.getSubContainer('spaces').visible = false;

		//The three sine phases share ONE state machine (reference fields —
		//the wave angle carries across the three stages):
		const sineState = new SineGridState();

		this._entries = createSpacesPhaseEntries(this._ctx, sineState);

		this._runner = new PhaseRunner(this._entries, {
			//_nextSpacesStage preamble: _reset (followParticle = null on the
			//whole pool), the lane-counter group cancel (see GROUP_SPACES) and
			//the next-phase analytics:
			onTransition: (index, phase) => {
				this._core.followIdx.fill(-1, this._ctx.range.start, this._ctx.range.end);
				this._deps.scripts.cancelGroup(GROUP_SPACES);

				Simple.event('home_spaces_next_phase', { phase: phase.name });
			},
			//_nextSpacesStage postamble — the colour cycle (particle tints +
			//the upward colouriseSpacesItems call). Entries flagged
			//ownsColours keep their authored tints (no production phase sets
			//it — see PhaseEntry.ownsColours):
			onStarted: (index) => {
				if (!this._entries[index]?.ownsColours) {
					this._colours.changeSpacesColour(this._ctx.range, this._ctx.activeCount);
				}
			},
			onCycleComplete: (totalPasses) => {
				Simple.event('home_spaces_full_phase_complete', { total_passes: totalPasses });
			}
		});
		this._runner.initAll();
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	public tick(): void {
		if (!this._active) return;

		//The stage counter/advance (reference _counter), then the frame
		//plumbing, then the current phase's own tick — see the framework
		//timing note in the class header:
		this._runner.tick(this._deps.context.deltaTime);

		this._positionParticleContainer();

		this._updateMask();
		this._resizeFilterArea();
		this._setReflectionBoundaryRatio();

		this._runner.phase.tick();
	}

	/** User is far enough right to be on the spaces scene. */
	public startAsActiveScene(reposition: boolean = true): void {
		this._positionParticleContainer();
		this._updateMask();

		//First half now, second half 20ms later (reference DelayHelpers are
		//UNGROUPED — a stop inside the window does not cancel them, kept):
		const midIndex = Math.round(this._ctx.activeCount / 2);

		for (let n = 0; n < midIndex; n++) {
			this._setSpacesStart(this._ctx.range.start + n);
		}

		DelayHelper.setTimeout(() => {
			for (let n = midIndex; n < this._ctx.activeCount; n++) {
				this._setSpacesStart(this._ctx.range.start + n);
			}
		}, 20);

		DelayHelper.setTimeout(() => {
			if (reposition) {
				//Stage 0 (Rain) armed now, counter parked 120 frames in:
				this._runner.startPhase(0);
				this._runner.setCounter(120);
			} else {
				//The from-work resume: counter parked 90 frames before stage
				//0's end; the phase itself is only armed on the FIRST ever
				//start (reference hasBeenStarted):
				if (!this._hasBeenStarted) {
					this._runner.startPhase(0);
				} else {
					this._runner.resume(0);
				}
				this._runner.setCounter((this._entries[0].frames as number) - 90);
			}

			this._active = true;
			this._hasBeenStarted = true;
		}, 40);

		DelayHelper.setTimeout(() => {
			this._deps.stage.getSubContainer('spaces').visible = true;
		}, 80);

		DelayHelper.setTimeout(() => {
			this._createRelectionFilter();
		}, 1000);
	}

	public stopAsActiveScene(): void {
		this._active = false;
		this._deps.stage.getSubContainer('spaces').visible = false;

		//hide() + reset() on the current phase (clean re-entry), then the
		//shared clears the reference stop implies (its deactivateAll sweep
		//kills the counter behaviours; the pending lane timers die here
		//where the reference lets them fire against deactivated lanes —
		//nothing observable either way):
		this._runner.stop();
		this._deps.scripts.cancelGroup(GROUP_SPACES);

		//pool.deactivateAllBehaviours(false) + active = false:
		const core = this._core;
		for (let i = this._ctx.range.start; i < this._ctx.range.end; i++) {
			core.behaviourMask[i] &= BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X;
			core.stateFlags[i] &= ~FLAG_ACTIVE;
		}
	}

	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	public resize(): void {}

	/** HomeField.setupForWork routes every NON-screens department here (the
	 *  SYSTEMS quirk included) — and the reference SpacesDepartment defines
	 *  no setupForWork of its own, so this is BaseDepartmentScene's no-op.
	 *  The reference's intentional nothing, kept. */
	public setupForWork(): void {}

	/** Reference SpacesDepartment.showFromWork, verbatim: full stop + full
	 *  restart (reposition = true default), re-forming the box stages as the
	 *  home scene slides back in from work. */
	public showFromWork(): void {
		this.stopAsActiveScene();
		this.startAsActiveScene();
	}

	/** DEAD in the reference (no callers — see the class header). */
	public fromWorkToHome(): void {}

	/** The facade routes the physics kernel's lerp-completion callback here
	 *  (ParticlePhysics.onTargetReached closures -> the context registry). */
	public onPhysicsTargetReached(lane: number): void {
		this._ctx.dispatchPhysicsTargetReached(lane);
	}

	//Private — frame plumbing
	//-----------------------------------------------------------------------------------------------

	/** setSpacesStart: _homeSpacesState's live prefix (repell.target =
	 *  spacesMouse — the facade's spaces repell centre; trackMouse is a
	 *  no-op setter) + disableTimelineX + activate(). */
	private _setSpacesStart(lane: number): void {
		this._core.behaviourMask[lane] |= BEHAVIOUR_DISABLE_TIMELINE_X;
		this._core.stateFlags[lane] |= FLAG_ACTIVE;
	}

	private _positionParticleContainer(): void {
		const sizes = this._deps.getSizes();
		const x = this._deps.context.containerX + sizes.zonePositions.spacesParticles.cx * sizes.virtualWidth;

		this._deps.stage.getSubContainer('spaces').x = x;
		this._deps.sceneProps.mouse.particleContainersX.spaces = x;
	}

	private _updateMask(): void {
		const spacesMask = this._deps.stage.spacesMask;
		const box = this._deps.getSizes().zonePositions.box;

		spacesMask.clear();

		spacesMask.beginFill('#fff000');

		const centerPx = this._deps.sceneProps.mouse.particleContainersX.spaces;

		spacesMask.x = centerPx - box.widthPx / 2;
		spacesMask.y = device.innerHeight * box.y;
		spacesMask.drawRect(0, 0, box.widthPx, box.heightPx);
		spacesMask.endFill();
		spacesMask.alpha = 0.4;
	}

	private _createRelectionFilter(): void {
		//globals.slowFpsMode SKIPS filter creation — and headless SwiftShader
		//TRIPS slowFpsMode, so any filter verification must run HEADED:
		if (this._relectionFilter || globals.slowFpsMode) {
			return;
		}

		this._relectionFilter = new (PIXI as any).filters.ReflectionFilter({
			amplitude: [1, 5],
			waveLength: [10, 10],
			alpha: [0.25, 0],
			boundary: 0.5,
			mirror: true
		});

		this._deps.stage.spacesOutter.filters = [this._relectionFilter];

		//Create a solid area to draw the filters over:
		this._filterShape = new PIXI.Graphics();
		this._filterShape.name = 'Screens Filter Area';
		this._deps.stage.spacesOutter.addChild(this._filterShape);
		this._filterShape.alpha = 0.00001;

		this._setReflectionBoundaryRatio();
	}

	private _setReflectionBoundaryRatio(): void {
		const box = this._deps.getSizes().zonePositions.box;
		const realBoundaryPx = box.y * device.innerHeight + box.heightPx;

		if (this._relectionFilter) {
			this._relectionFilter.boundary = realBoundaryPx / device.innerHeight + 0.015;
		}
	}

	private _resizeFilterArea(): void {
		if (!this._filterShape) {
			return;
		}

		//Full-screen redraw every tick, like the reference:
		this._filterShape.clear();
		this._filterShape.beginFill('#ff0000');
		this._filterShape.drawRect(0, 0, device.innerWidth, device.innerHeight);
		this._filterShape.endFill();
	}
}
